import { Portal } from '@chakra-ui/react';
import { getValidChildren } from '@chakra-ui/react-utils';
import { forwardRef, HTMLChakraProps, useMultiStyleConfig } from '@chakra-ui/system';
import { IMenuProps, Menu } from '@lego/klik-ui-menu';
import { Tag } from '@lego/klik-ui-tag';
import * as React from 'react';
import { DropdownButton } from './DropdownButton';
import { DropdownGroup, IDropdownGroupProps } from './DropdownGroup';
import { DropdownList } from './DropdownList';
import { IDropdownOptionProps } from './DropdownOption';
import { IDropdownSectionProps } from './DropdownSection';
import {
  IDropdownOptionItem,
  IDropdownSelectionIndicatorAlignment,
  IDropdownSelectionIndicatorType,
  IDropdownSizes,
} from './DropdownTypes';
import {
  buildDeepOptionsDict,
  getGroupDecorator,
  getGroupType,
  getShouldCloseOnSelect,
  manageSelection,
  selectionLabels,
  selectionValues,
  selectionValuesForGroup,
  stringArraysEqual,
} from './utils';

type OmittedProps =
  | 'arrowPadding'
  | 'boundary'
  | 'children'
  | 'computePositionOnMount'
  | 'defaultIsOpen'
  | 'eventListeners'
  | 'gutter'
  | 'matchWidth'
  | 'modifiers'
  | 'offset'
  | 'onChange'
  | 'placement'
  | 'preventOverflow'
  | 'size'
  | 'strategy'
  | 'variant';

export type AllowedChildrenType =
  | IDropdownGroupProps
  | IDropdownOptionProps
  | IDropdownSectionProps;

type LayoutProps = Omit<HTMLChakraProps<'div'>, OmittedProps>;
export interface IDropdownComponentProps extends Omit<IMenuProps, OmittedProps>, LayoutProps {
  /**
   * Allow multiple selections?
   * @default false
   */
  isMultiple?: boolean;

  /**
   * Is the Dropdown disabled?
   * @default false
   */
  isDisabled?: boolean;

  /**
   * Is the selection invalid?
   * @default false
   */
  isInvalid?: boolean;

  /**
   * Should the Menu be open by default?
   * @default false
   */
  isOpen?: boolean;

  /**
   * Initial text in the Dropdown Input field
   */
  placeholder?: string;

  /**
   * Texts to display "2x %here%"
   * @default Options selected
   */
  selectionString?: string;

  /**
   * Render an inline Tag to clear the selection. (multi-selects only)
   */
  shouldDisplaySelectionInline?: boolean;

  /**
   * Display the `text` of the selected items instead of $selectionString
   */
  shouldDisplaySelectionLabels?: boolean;

  /**
   * Which icon/marker to use for indicating the selected option(s)
   * @default tick single
   */
  selectionIndicator?: IDropdownSelectionIndicatorType;

  /**
   * How to vertically align the selectionIndicator icon/marker
   * @default center
   */
  selectionIndicatorAlignment?: IDropdownSelectionIndicatorAlignment;

  /**
   * Initially selected item(s)
   */
  defaultValue?: string[];

  /**
   * The value of the Dropdown in controlled mode
   */
  value?: string[];

  /**
   * Set the design-system size of the root-component
   * @default md
   */
  size?: IDropdownSizes;

  /**
   * Set a fixed width of the Dropdown List
   * @default 100%
   */
  listWidth?: string;

  /**
   * Set a fixed height of the Dropdown List
   * @default 90vh
   */
  listHeight?: string;

  /**
   * Action to call when the selection changes
   * @param value will hold the value-prop of the selected item(s)
   */
  onChange?: (value: string[]) => void;

  /**
   * Allowed children
   * @default Dropdown.Group | Dropdown.Option | Dropdown.Divider
   */
  children: React.ReactElement<AllowedChildrenType> | React.ReactElement<AllowedChildrenType>[];

  // width?: Token<CSS.Property.Width | number, 'sizes'>;
  // width?: string;
}

export const DropdownComponent = forwardRef<IDropdownComponentProps, 'div'>((props, ref) => {
  const {
    width = '100%',
    listHeight: maxHeight = '90vh',
    listWidth = undefined,
    placeholder: placeholderProp = undefined,
    selectionString = 'Options selected',
    isDisabled = false,
    isMultiple = false,
    isInvalid = false,
    isOpen: isOpenProp = false,
    size = 'md',
    selectionIndicator = 'tick',
    selectionIndicatorAlignment = 'center',
    onChange: onChangeProp = (_value: string[] | string) => void {},
    defaultValue: defaultValueProp,
    value: valueProp,
    shouldDisplaySelectionInline = true,
    shouldDisplaySelectionLabels = undefined,
    children,
    ...rest
  } = props;

  // ---

  const defaultValue = valueProp ?? defaultValueProp;

  const styles = useMultiStyleConfig('Dropdown', { ...rest, size });
  const defaultPlaceholder = isMultiple ? 'Choose option(s)...' : 'Choose option...';
  const placeholder = placeholderProp ?? defaultPlaceholder;

  // Determine whether the List should stay open after a selection.
  const shouldCloseOnSelect = React.useMemo(() => {
    return getShouldCloseOnSelect(children, isMultiple);
  }, [children, isMultiple]);

  // Some code-paths are different when we have multiple option groups
  const groupCount = React.useMemo(() => {
    return getValidChildren(children).filter(
      (childNode): childNode is React.ReactElement<IDropdownGroupProps> =>
        childNode.type === DropdownGroup
    ).length;
  }, [children]);

  // Internal list of all options from all groups
  let optionsDict: IDropdownOptionItem[] = [];

  // Flag to prevent defaultValues to interfere with an empty selection value
  const obeyDefaultValueRef = React.useRef<boolean>(true);
  const previousValueRef = React.useRef<string[]>([]);

  // internal selection store, with helper Ref to overcome the async React.SetStateAction
  const [selection, setSelection] = React.useState<IDropdownOptionItem[]>([]);
  const selectionRef = React.useRef(selection);

  React.useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  // internal store to keep track of the index of the selected item
  const selectionIndexRef = React.useRef<number>(-1);

  // note: $group is only used if the selectedValues is an empty array (to clear the right group)
  const handleValueChanged = React.useCallback((selectedValues: string[], group: string) => {
    // Find the optionsDict entry for the selected value(s)
    const filteredOptions: IDropdownOptionItem[] = [];

    // Count through, to find the selectedIndex
    selectionIndexRef.current = -1;

    selectedValues.forEach((val: string) => {
      optionsDict.forEach((opt: IDropdownOptionItem, index) => {
        if (opt.value === val) {
          filteredOptions.push(opt);
          selectionIndexRef.current = index;
        }
      });
    });

    const newSelection = manageSelection(selectionRef.current, filteredOptions, group);

    // forcefully clear selectionRef, as its one update cycle behind...
    if (newSelection.length === 0) {
      selectionRef.current = [];
    }

    setSelection(newSelection);

    const publicSelection = selectionValues(newSelection);
    const hasEqualSelection = stringArraysEqual(previousValueRef.current, publicSelection);

    if (!obeyDefaultValueRef.current && !hasEqualSelection) {
      onChangeProp(publicSelection);
    }

    // default values have been applied at first render. Don't bother with them anymore
    obeyDefaultValueRef.current = false;
  }, []);

  // Called from useDropdownOptionGroup
  const handleOnChange = React.useCallback(
    (optionValue: string[] | string, optionGroup: string) => {
      optionValue = Array.isArray(optionValue) ? optionValue : [optionValue];
      handleValueChanged(optionValue, optionGroup);
    },
    [children]
  );

  // Called by the Tag inside the Input
  const clearSelection = React.useCallback(() => {
    handleValueChanged([''], 'CLEARALL');
  }, [selection, setSelection]);

  // For most use-cases, the DropdownOptions will be provided without a wrapping DropdownGroup
  const filledChildren =
    groupCount === 0 ? <DropdownGroup groupid="grp-0">{children}</DropdownGroup> : children;

  const defaultValues: IDropdownOptionItem[] = [];

  // Our DropdownGroups needs a few key properties set
  const augmentedChildren: React.ReactElement<AllowedChildrenType>[] = getValidChildren(
    filledChildren
  ).map((child: React.ReactElement<AllowedChildrenType>, childIterator) => {
    if (child.type === DropdownGroup) {
      const groupProps = child.props as IDropdownGroupProps;
      const groupid = `grp-${childIterator}`;
      const type = getGroupType(isMultiple, groupProps.isMultiple);
      const isGroupMultiple: boolean = type === 'checkbox';
      const value = selectionValuesForGroup(selection, groupid, isGroupMultiple);

      const groupSelectionIndicator = getGroupDecorator(
        selectionIndicator,
        groupProps.selectionIndicator,
        isMultiple,
        groupProps.isMultiple
      );

      const newGroupProps: IDropdownGroupProps = {
        ...groupProps,
        selectionIndicator: groupSelectionIndicator,
        selectionIndicatorAlignment,
        type,
        size,
        value,
        onOptionSelected: handleOnChange,
        isMultiple,
        width: listWidth,
        groupid,
      };

      if (!obeyDefaultValueRef.current) {
        newGroupProps.defaultValue = '';
      }

      // Determine defaultValues for the group
      let groupDefaultValues: string[] = [];

      if (obeyDefaultValueRef.current) {
        // Use the root-ones
        if (defaultValue) {
          groupDefaultValues = defaultValue;
        }

        // ...but override with group-ones if present
        if (groupProps.defaultValue) {
          const groupDefultValueAsArray: string[] = Array.isArray(groupProps.defaultValue)
            ? groupProps.defaultValue
            : [groupProps.defaultValue];

          groupDefaultValues = groupDefultValueAsArray;
        }
      }

      const groupOptionType = type === 'checkbox' ? 'm' : 's';

      for (const item of groupDefaultValues) {
        defaultValues.push({
          text: type,
          value: item,
          _type: groupOptionType,
          _group: groupid,
        } as IDropdownOptionItem);
      }

      return React.cloneElement(child, newGroupProps);
    } else {
      return child;
    }
  });

  React.useEffect(() => {
    const cmp = valueProp ?? defaultValueProp ?? [];

    if (!stringArraysEqual(previousValueRef.current, cmp)) {
      obeyDefaultValueRef.current = true;
      previousValueRef.current = cmp;

      const safeDefaultValue = defaultValue ?? [];

      if (safeDefaultValue.length) {
        handleValueChanged(safeDefaultValue, '');
      } else {
        handleValueChanged([''], 'CLEARALL');
      }

      obeyDefaultValueRef.current = false;
    }
  }, [valueProp, defaultValueProp, defaultValue, previousValueRef]);

  React.useEffect(() => {
    optionsDict = buildDeepOptionsDict(isMultiple, filledChildren);

    if (!selectionRef.current.length) {
      const keys: string[] = [];

      for (const item of defaultValues) {
        if (!keys.includes(item.value)) {
          keys.push(item.value);
        }
      }

      if (keys.length) {
        handleValueChanged(keys, '');
      }
    }
  }, [filledChildren]);

  // Determine what to render as the placeholder text
  const placeholderValue = React.useMemo(() => {
    if (shouldDisplaySelectionLabels) {
      return selection.length === 0 ? placeholder : selectionLabels(selection).join(', ');
    }

    if (shouldDisplaySelectionLabels === false) {
      return placeholder;
    }

    if (selection.length > 0) {
      if (isMultiple || groupCount > 0) {
        return selectionString;
      } else {
        return selection[0].text;
      }
    } else {
      return placeholder;
    }
  }, [selection, isMultiple, placeholder, selectionString]);

  // TODO: This could use some more work...
  // Determine what to render as the aria-label text
  const ariaLabelValue = React.useMemo(() => {
    if (shouldDisplaySelectionLabels) {
      return selection.length === 0
        ? placeholder
        : `${selectionLabels(selection).join(' and ')} selected`;
    }

    if (selection.length > 0) {
      if (isMultiple || groupCount > 0) {
        if (selection.length === 1) {
          return `${selection.length} option selected`;
        } else {
          return `${selection.length} options selected`;
        }
      } else {
        return `${selection[0].text} selected`;
      }
    } else {
      return placeholder;
    }
  }, [selection, isMultiple, placeholder, selectionString]);

  // Determine if the Tag should be rendered
  const willDisplaySelectionInline = React.useMemo(() => {
    return shouldDisplaySelectionInline && selection.length > 0 && (isMultiple || groupCount > 0);
  }, [selection, isMultiple]);

  const selectedIndex = selectionIndexRef.current;
  const shouldAutoSelect: boolean = selectedIndex === -1;

  return (
    <Menu
      autoSelect={shouldAutoSelect}
      closeOnSelect={shouldCloseOnSelect}
      defaultIsOpen={isOpenProp}
      matchWidth={true}
      preventOverflow={false}
      {...props}
    >
      <React.Fragment>
        <DropdownButton
          aria-multiselectable={isMultiple}
          ariaLabel={ariaLabelValue}
          hasInlineSelection={willDisplaySelectionInline}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          label={placeholderValue}
          ref={ref}
          selectedIndex={selectedIndex}
          size={size}
          width={width}
        />

        {willDisplaySelectionInline && (
          <Tag size={size} sx={{ direction: 'ltr', ...styles.selectioncount }}>
            <Tag.Label sx={styles.selectioncountLabel}>{selection.length}</Tag.Label>
            <Tag.CloseButton onClick={clearSelection} tabIndex={0} />
          </Tag>
        )}

        <Portal>
          <DropdownList maxHeight={maxHeight} width={listWidth}>
            {augmentedChildren}
          </DropdownList>
        </Portal>
      </React.Fragment>
    </Menu>
  );
});
